import * as React from 'react';
import {
  Checkbox,
  Icon,
  Input,
  InputWrapper,
  Tabs,
  Tooltip,
} from '@teamsnap/snap-ui';
import {
  BLANK_OPTION,
  DEPOSIT_TYPE_SELECT_OPTIONS,
  FREQUENCY_TYPE_SELECT_OPTIONS,
  INSTALLMENT_COUNT_OPTIONS,
  WEEKLY_SELECT_OPTIONS,
  MONTHLY_SELECT_OPTIONS,
} from '../installment-utils';
import { PaymentPlanFormValues } from './types';
import { PaymentSchedule } from './PaymentSchedule';

interface PaymentPlansFormProps {
  formValues: PaymentPlanFormValues | null;
  updateField: (field: string, value: any) => void;
  errors?: Record<string, string[]>;
  currencyCode?: string;
}

const PerRegistrantTooltip = () => (
  <Tooltip
    align="center"
    content="This fee applies to each participant registered for the program. If unchecked, the amount applies once per cart, regardless of the number of participants registered."
    side="bottom"
  >
    <Icon name="info" className="sui-text-action-icon" />
  </Tooltip>
);

export const DepositFields = ({
  formValues,
  updateField,
  errors,
  currencyCode,
}: PaymentPlansFormProps) => {
  const depositPercentRef = React.useRef<HTMLInputElement>(null);
  const depositAmountRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (!errors?.depositType) return; // temp test of "touched" field
    if (formValues?.depositType === 'percentage' && depositPercentRef.current) {
      depositPercentRef.current.focus();
    } else if (
      formValues?.depositType === 'amount' &&
      depositAmountRef.current
    ) {
      depositAmountRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.depositType, errors?.depositType]);
  return (
    <>
      <InputWrapper
        label="Deposit"
        name="depositType"
        required
        className={`Deposit-Input--${formValues?.depositType} sui-w-1/2 sui-my-2`}
      >
        <div className="sui-flex">
          <Input
            name="depositType"
            type="select"
            options={DEPOSIT_TYPE_SELECT_OPTIONS}
            value={formValues?.depositType}
            onChange={(e) => {
              updateField('depositType', e.target.value);
            }}
          />
          {formValues?.depositType === 'amount' && (
            <Input
              ref={depositAmountRef}
              name="depositAmount"
              required
              currency={currencyCode}
              type="financial"
              inputProps={{
                min: 0,
                defaultValue: formValues?.depositAmount || '',
              }}
              onBlur={(e) => updateField('depositAmount', e.target.value)}
              className="sui-w-5/12 sui-shrink-0"
              errors={errors?.depositAmount}
              allowDecimals
            />
          )}

          {formValues?.depositType === 'percentage' && (
            <Input
              ref={depositPercentRef}
              name="depositAmount"
              required
              type="number"
              inputProps={{
                min: 1,
                max: 100,
                defaultValue: formValues?.depositAmount || '',
              }}
              onBlur={(e) => updateField('depositAmount', e.target.value)}
              className="sui-w-5/12 sui-shrink-0 depositAmount-percentage"
              errors={errors?.depositAmount}
            />
          )}
        </div>
        {formValues?.depositType === 'amount' && (
          <div className="sui-flex sui-items-center sui-gap-1 sui-mt-1">
            <Checkbox
              label="Apply deposit per participant"
              name="applyDepositPerRegistration"
              value="applyDepositPerRegistration"
              extraProps={{
                input: {
                  checked: formValues?.applyDepositPerRegistration,
                  onChange: (e) =>
                    updateField(
                      'applyDepositPerRegistration',
                      e.target.checked
                    ),
                },
              }}
            />
            <PerRegistrantTooltip />
          </div>
        )}
      </InputWrapper>
    </>
  );
};

export const InstallmentCountField = ({
  formValues,
  updateField,
  errors,
}: PaymentPlansFormProps) => (
  <Input
    label="Number of installments"
    name="installmentCount"
    type="select"
    required
    options={[BLANK_OPTION, ...INSTALLMENT_COUNT_OPTIONS]}
    value={formValues?.installmentCount}
    onChange={(e) => updateField('installmentCount', e.target.value)}
    className="sui-w-1/2 sui-mb-2"
    errors={errors?.installmentCount}
  />
);

export const PlanNameField = ({
  formValues,
  updateField,
  errors,
}: PaymentPlansFormProps) => {
  const nameRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (nameRef.current && !formValues?.name) {
      nameRef.current.focus();
    }
  }, []);
  return (
    <Input
      ref={nameRef}
      label="Plan name"
      name="paymentPlanName"
      type="text"
      required
      placeholder="Plan name"
      value={formValues?.name}
      inputProps={{
        maxLength: 255,
        autoComplete: 'off',
      }}
      onBlur={(e) => updateField('name', e.target.value.trim())}
      onChange={(e) => updateField('name', e.target.value)}
      className="sui-w-1/2 sui-my-2"
      errors={errors?.name}
    />
  );
};

const withdrawBasedOnCheckoutOption = {
  value: 'withdrawBasedOnCheckout',
  label: 'Same date as checkout date',
};

export const DynamicDatesFields = ({
  formValues,
  updateField,
  errors,
}: PaymentPlansFormProps) => (
  <InputWrapper
    label="Installment dates"
    name="frequencyType"
    required
    errors={
      errors?.frequencyType ||
      errors?.withdrawDayMonthly ||
      errors?.withdrawDayWeekly
    }
    className="sui-w-full sui-my-2"
  >
    <div className="sui-flex sui-items-center sui-gap-2">
      <Input
        name="frequencyType"
        type="select"
        options={FREQUENCY_TYPE_SELECT_OPTIONS}
        value={formValues?.frequencyType}
        className="sui-w-1/4 sui-shrink-0"
        onChange={(e) => updateField('frequencyType', e.target.value)}
      />
      <span>on</span>
      {formValues?.frequencyType === 'monthly' && (
        <>
          <Input
            name="withdrawDayMonthly"
            required
            type="select"
            value={formValues?.withdrawDayMonthly}
            options={[withdrawBasedOnCheckoutOption, ...MONTHLY_SELECT_OPTIONS]} // days of the month
            onChange={(e) => updateField('withdrawDayMonthly', e.target.value)}
            className={
              formValues?.withdrawDayMonthly === 'withdrawBasedOnCheckout'
                ? 'sui-w-5/12 sui-shrink-0'
                : 'sui-w-1/4 sui-shrink-0'
            }
            errors={errors?.withdrawDayMonthly}
          />
          {formValues?.withdrawDayMonthly !== 'withdrawBasedOnCheckout' && (
            <span>day of the month</span>
          )}
        </>
      )}
      {['weekly', 'biweekly'].includes(formValues?.frequencyType) && (
        <Input
          name="withdrawDayWeekly"
          required
          type="select"
          value={formValues?.withdrawDayWeekly}
          options={[withdrawBasedOnCheckoutOption, ...WEEKLY_SELECT_OPTIONS]}
          onChange={(e) => updateField('withdrawDayWeekly', e.target.value)}
          className={
            formValues?.withdrawDayWeekly === 'withdrawBasedOnCheckout'
              ? 'sui-w-5/12 sui-shrink-0'
              : 'sui-w-1/4 sui-shrink-0'
          }
          errors={errors?.withdrawDayWeekly}
        />
      )}
    </div>
  </InputWrapper>
);

export const PaymentPlansForm = (props: PaymentPlansFormProps) => {
  const { formValues, updateField } = props;
  return (
    <form className="PaymentPlansForm sui-w-full" data-testid="PaymentPlansForm">
      <PlanNameField {...props} />
      <Tabs
        defaultValue={formValues?.planType}
        onChange={(value) => updateField('planType', value)}
        data-testid="PaymentPlansForm--tabs"
        tabs={[
          {
            content: (
              <>
                <DepositFields {...props} />
              </>
            ),
            label: 'Custom schedule',
            value: 'custom',
          },
          {
            content: (
              <>
                <DepositFields {...props} />
                <InstallmentCountField {...props} />
                <DynamicDatesFields {...props} />
              </>
            ),
            label: 'Dynamic schedule',
            value: 'dynamic',
          },
        ]}
      />
      <PaymentSchedule {...props} />
    </form>
  );
};
